import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { usePostHog } from 'posthog-js/react';
import { ThemeContext } from '../ThemeContext';

// Minimal frontmatter parser — handles strings, inline arrays, and block arrays.
// Avoids js-yaml (and its Node polyfill dependencies) while covering the YAML
// subset used in the case study .md files.
const parseFrontmatter = (text) => {
  const match = text.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/);
  if (!match) return { attributes: {}, body: text };
  const [, yaml, body] = match;
  const attrs = {};
  let currentKey = null;
  let blockArray = null;
  for (const raw of yaml.split('\n')) {
    const line = raw.trimEnd();
    if (!line || line.startsWith('#')) continue;
    if ((line.startsWith('  - ') || line.startsWith('- ')) && currentKey) {
      const val = line.replace(/^\s*-\s*/, '');
      if (blockArray !== null) blockArray.push(parseYamlValue(val));
      continue;
    }
    const kv = line.match(/^(\w[\w-]*):\s*(.*)?$/);
    if (kv) {
      if (blockArray !== null) attrs[currentKey] = blockArray;
      currentKey = kv[1];
      const rest = (kv[2] || '').trim();
      if (rest.startsWith('[')) {
        attrs[currentKey] = rest.slice(1, rest.lastIndexOf(']'))
          .split(',').map(s => s.trim().replace(/^['"]|['"]$/g, ''));
        blockArray = null;
      } else if (rest === '') {
        blockArray = [];
        attrs[currentKey] = blockArray;
      } else {
        attrs[currentKey] = parseYamlValue(rest);
        blockArray = null;
      }
    }
  }
  if (blockArray !== null) attrs[currentKey] = blockArray;
  return { attributes: attrs, body: body.trimStart() };
};

const parseYamlValue = (val) => {
  if (!val) return '';
  if (val.startsWith('{')) {
    const obj = {};
    const inner = val.slice(1, val.lastIndexOf('}')).trim();
    inner.split(/,\s*(?=\w+:)/).forEach(pair => {
      const idx = pair.indexOf(':');
      if (idx === -1) return;
      const k = pair.slice(0, idx).trim();
      const v = pair.slice(idx + 1).trim().replace(/^['"]|['"]$/g, '');
      obj[k] = v;
    });
    return obj;
  }
  return val.replace(/^['"]|['"]$/g, '');
};

// Module-level cache so re-opening a window doesn't re-fetch.
const cache = new Map();

const Container = styled.div`
  padding: 24px;
  overflow-y: auto;
  height: 100%;
`;

const Hero = styled.img`
  width: 100%;
  max-height: 220px;
  object-fit: cover;
  border-radius: 6px;
  margin-bottom: 20px;
`;

const Title = styled.h1`
  font-size: 1.6rem;
  margin: 0 0 8px;
  color: ${p => p.theme === 'light' ? '#111' : '#f0f0f0'};
`;

const Summary = styled.p`
  font-size: 0.95rem;
  color: ${p => p.theme === 'light' ? '#666' : '#aaa'};
  margin: 0 0 12px;
  line-height: 1.5;
`;

const Role = styled.div`
  font-size: 0.72rem;
  color: ${p => p.theme === 'light' ? '#888' : '#888'};
  margin: 0 0 18px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 600;
`;

const OutcomeCallout = styled.div`
  background: ${p => p.theme === 'light' ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.05)'};
  border-left: 3px solid var(--accent-color);
  padding: 12px 16px;
  border-radius: 4px;
  margin: 0 0 20px;
  font-size: 0.92rem;
  line-height: 1.55;
  color: ${p => p.theme === 'light' ? '#333' : '#d0d0d0'};

  strong {
    display: block;
    font-size: 0.68rem;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--accent-color);
    margin-bottom: 4px;
    font-weight: 700;
  }
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
`;

const StackPill = styled.span`
  background: ${p => p.theme === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.08)'};
  color: ${p => p.theme === 'light' ? '#444' : '#ccc'};
  border-radius: 20px;
  padding: 3px 10px;
  font-size: 0.8rem;
  font-weight: 500;
`;

const LinksRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 24px;
`;

const ExternalLink = styled.a`
  display: inline-block;
  background-color: var(--accent-color);
  color: white !important;
  padding: 7px 14px;
  text-decoration: none;
  border-radius: 4px;
  font-size: 0.85rem;
  font-weight: 500;
  transition: background-color 0.2s, transform 0.15s;

  &:hover {
    background-color: var(--accent-hover);
    transform: translateY(-1px);
  }
`;

const InternalLink = styled.a`
  display: inline-block;
  border: 1px solid ${p => p.theme === 'light' ? '#ccc' : '#555'};
  color: ${p => p.theme === 'light' ? '#333' : '#ddd'} !important;
  padding: 7px 14px;
  text-decoration: none;
  border-radius: 4px;
  font-size: 0.85rem;
  font-weight: 500;
  transition: border-color 0.2s, transform 0.15s;

  &:hover {
    border-color: var(--accent-color);
    transform: translateY(-1px);
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${p => p.theme === 'light' ? '#eee' : '#333'};
  margin: 0 0 24px;
`;

const MarkdownBody = styled.div`
  font-size: 0.95rem;
  line-height: 1.7;
  color: ${p => p.theme === 'light' ? '#333' : '#d0d0d0'};

  h2 {
    font-size: 1.1rem;
    font-weight: 700;
    margin: 28px 0 10px;
    color: ${p => p.theme === 'light' ? '#111' : '#f0f0f0'};
  }

  h3 {
    font-size: 1rem;
    font-weight: 600;
    margin: 20px 0 8px;
    color: ${p => p.theme === 'light' ? '#222' : '#e0e0e0'};
  }

  p { margin: 0 0 14px; }

  strong { color: ${p => p.theme === 'light' ? '#111' : '#eee'}; }

  code {
    background: ${p => p.theme === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.1)'};
    border-radius: 3px;
    padding: 1px 5px;
    font-size: 0.88em;
    font-family: 'Monaco', 'Consolas', monospace;
  }

  ul, ol {
    padding-left: 20px;
    margin: 0 0 14px;
    li { margin-bottom: 5px; }
  }

  blockquote {
    border-left: 3px solid var(--accent-color);
    margin: 0 0 14px;
    padding: 4px 12px;
    color: ${p => p.theme === 'light' ? '#666' : '#aaa'};
  }

  a {
    color: var(--accent-color);
    text-decoration: underline;
  }
`;

const StatusText = styled.p`
  color: ${p => p.theme === 'light' ? '#888' : '#666'};
  font-size: 0.9rem;
  padding: 40px 0;
  text-align: center;
`;

const RetryButton = styled.button`
  display: block;
  margin: 10px auto 0;
  padding: 7px 18px;
  border: 1px solid ${p => p.theme === 'light' ? '#ccc' : '#555'};
  background: none;
  color: ${p => p.theme === 'light' ? '#333' : '#ddd'};
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.85rem;
`;

const CaseStudyWindow = ({ slug }) => {
  const { theme } = useContext(ThemeContext);
  const posthog = usePostHog();
  const [data, setData] = useState(cache.get(slug) ?? null);
  const [status, setStatus] = useState(data ? 'loaded' : 'loading');

  const load = async () => {
    setStatus('loading');
    try {
      const res = await fetch(`/case-studies/${slug}.md`);
      if (!res.ok) throw new Error(`${res.status}`);
      const text = await res.text();
      const result = parseFrontmatter(text);
      cache.set(slug, result);
      setData(result);
      setStatus('loaded');
      posthog?.capture('case_study_opened', { slug });
    } catch {
      posthog?.capture('case_study_load_failed', { slug });
      setStatus('error');
    }
  };

  useEffect(() => {
    if (!data) load();
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (status === 'loading') {
    return <StatusText theme={theme}>Loading…</StatusText>;
  }

  if (status === 'error' || !data) {
    return (
      <Container>
        <StatusText theme={theme}>Couldn't load case study.</StatusText>
        <RetryButton theme={theme} onClick={() => { posthog?.capture('case_study_retry_clicked', { slug }); load(); }}>Retry</RetryButton>
      </Container>
    );
  }

  const { attributes: a, body } = data;
  const isExternal = (url) => url.startsWith('http');

  return (
    <Container>
      {a.hero && <Hero src={a.hero} alt={a.title} />}
      <Title theme={theme}>{a.title}</Title>
      {a.summary && <Summary theme={theme}>{a.summary}</Summary>}
      {a.role && <Role theme={theme}>{a.role}</Role>}
      {a.outcome && (
        <OutcomeCallout theme={theme}>
          <strong>Outcome</strong>
          {a.outcome}
        </OutcomeCallout>
      )}

      {a.stack?.length > 0 && (
        <Meta>
          {a.stack.map(s => <StackPill key={s} theme={theme}>{s}</StackPill>)}
        </Meta>
      )}

      {a.links?.length > 0 && (
        <LinksRow>
          {a.links.map(({ label, url }) =>
            isExternal(url) ? (
              <ExternalLink
                key={label}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => posthog?.capture('case_study_link_clicked', { slug, label, url, type: 'external' })}
              >
                {label}
              </ExternalLink>
            ) : (
              <InternalLink
                key={label}
                href={url}
                theme={theme}
                onClick={() => posthog?.capture('case_study_link_clicked', { slug, label, url, type: 'internal' })}
              >
                {label}
              </InternalLink>
            )
          )}
        </LinksRow>
      )}

      <Divider theme={theme} />

      <MarkdownBody theme={theme}>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{body}</ReactMarkdown>
      </MarkdownBody>
    </Container>
  );
};

export default CaseStudyWindow;
